import { useEffect, useState } from 'react';

import ExportedImage from 'next-image-export-optimizer';

import { motion } from 'framer-motion';

import AnimateWhileInView from '@/components/shared/AnimateWhileInView';

import { siteData } from '@/data/configs/site';
import uniqueProtocolCombinations from '@/data/supported-assets-data-unique-protocol-combinations.json';
import trustLessFrameSmall from '@/public/images/lp/home/trustless-frame-small.png';
import trustLessFrame from '@/public/images/lp/home/trustless-frame.png';
import gradientLinkArrow from '@/public/images/lp/svgs/gradient-link-arrow.svg';

const ProtocolSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const protocols = uniqueProtocolCombinations.map((protocol) => ({
    name: protocol.name,
    type: protocol.type,
    icon: `/images/lp/coins/icons/${protocol.platform.toLowerCase()}.png`,
  }));

  const displayedProtocols = searchQuery
    ? protocols.filter(
        (protocol) =>
          protocol.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          protocol.type.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : protocols;

  return (
    <div className='relative w-full -md:mx-auto -md:w-[340px]'>
      <ExportedImage
        placeholder='empty'
        src={trustLessFrame}
        alt='trustless-frame'
        className='relative z-[-10] h-auto md:w-[549px] -md:hidden'
      />
      <ExportedImage
        placeholder='empty'
        src={trustLessFrameSmall}
        alt='trustless-frame-small'
        className='relative z-[-10] h-[400px] w-[340px] md:hidden'
      />
      <div className='absolute left-[30px] top-[30px] z-10 h-[60%] w-[75%]'>
        <div className='flex items-center justify-between pb-3'>
          <div className='font-sans text-base font-bold leading-[1.25em] text-white -sm:pl-2 -sm:pt-2'>
            Search Protocols
          </div>
        </div>

        <div className='flex flex-col gap-2 rounded-[20px] p-2 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] sm:gap-4'>
          <div className='relative w-full'>
            <input
              type='text'
              className='w-full rounded-[15px] border-none bg-[#060C17] py-1 pl-10 pr-4 text-sm text-white placeholder-[#797b89] focus:outline-none focus:ring-1 focus:ring-[#6066A3]'
              placeholder='Search protocols...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className='absolute inset-y-0 left-0 flex items-center pl-3'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                className='h-4 w-4'
              >
                <path
                  d='M10.6294 10.3053L13.3354 12.9997M12.0018 6.99967C12.0018 4.42235 9.91246 2.33301 7.33512 2.33301C4.7578 2.33301 2.66846 4.42235 2.66846 6.99967C2.66846 9.57701 4.7578 11.6663 7.33512 11.6663C9.91246 11.6663 12.0018 9.57701 12.0018 6.99967Z'
                  stroke='#797B89'
                  strokeWidth='0.833333'
                  strokeLinecap='round'
                />
              </svg>
            </div>
          </div>
          <div className='h-[240px] overflow-y-auto pr-4 md:max-h-[220px] -md:mt-2'>
            <div className='flex flex-col gap-[9.73px]'>
              {displayedProtocols.map((protocol, index) => (
                <div key={`${protocol.name}-${index}`}>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-[12.97px]'>
                      <div className='relative h-[38.91px] w-[38.91px] overflow-hidden rounded-[8.92px] border border-[#ffffff]/30'>
                        <ExportedImage
                          placeholder='empty'
                          src={protocol.icon}
                          alt={protocol.name}
                          className='absolute inset-0 m-auto h-[24.32px] w-[24.32px] rounded-[25.34px] object-contain'
                          width={40}
                          height={40}
                        />
                      </div>
                      <div className='flex flex-col gap-[3.24px]'>
                        <div className='font-sans text-[14.78px] font-bold leading-[1.176em] text-[#ADAFC4]'>
                          {protocol.name}
                        </div>
                        <div className='font-sans text-[12.35px] font-medium leading-[1.429em] text-[#E6BC41]'>
                          {protocol.type}
                        </div>
                      </div>
                    </div>
                  </div>
                  {index < displayedProtocols.length - 1 && (
                    <div className='mt-2 h-0 w-full outline outline-[0.81px] outline-offset-[-0.41px] outline-[#355983]/30'></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Trustless = () => {
  return (
    <section className='mx-auto my-10 flex items-center justify-between gap-x-[97px] text-white lg:my-24 lg:w-full 2xl:my-36 -lg:flex-col-reverse'>
      <AnimateWhileInView
        delay={0.3}
        classNaming='relative w-full md:w-[549px] md:min-w-[549px] -lg:mx-auto'
      >
        <ProtocolSelector />
      </AnimateWhileInView>
      <div className='flex flex-col space-y-4 md:w-auto -lg:mb-12 -lg:w-[80%] -lg:items-center -lg:text-center -sm:w-auto'>
        <AnimateWhileInView delay={0.1}>
          <h2 className='home-small-header font-sans text-xs font-normal uppercase sm:text-sm xl:text-base 2xl:text-lg'>
            Trustless
          </h2>
        </AnimateWhileInView>
        <AnimateWhileInView delay={0.15}>
          <h3 className='font-heading text-3xl font-bold sm:text-4xl lg:text-3xl xl:text-5xl'>
            Widest Blockchain Protocol Support
          </h3>
        </AnimateWhileInView>
        <AnimateWhileInView delay={0.2}>
          <p className='text-xl -lg:text-lg -lg:leading-[1.176em]'>
            Trade any supported assets across various protocols in a fully non-custodial and
            decentralized manner. Be your own exchange!
          </p>
        </AnimateWhileInView>
        <AnimateWhileInView delay={0.25}>
          <motion.a
            target='_blank'
            href='/en/docs/komodo-defi-framework/tutorials/listing-a-new-coin/'
            className='trustless-gradient flex flex-row rounded-md text-xl font-bold underline xl:items-center -xl:flex-col -lg:items-center -lg:text-lg'
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          >
            List your coin/token on&nbsp;{' '}
            <span className='flex flex-row items-center'>
              Komodo Wallet
              <ExportedImage
                placeholder='empty'
                src={gradientLinkArrow}
                className='ml-2 h-[19px] w-auto'
                width={16}
                height={17}
                alt='link'
              />
            </span>
          </motion.a>
        </AnimateWhileInView>
      </div>
    </section>
  );
};

export default Trustless;
